import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MessageListProps } from '../../../types/chat';
import MessageItem from './MessageItem';

const MessageList: React.FC<MessageListProps> = React.memo(({
  messages,
  currentUserId,
  onLoadMore,
  onMessageLongPress,
  onImagePress,
  onReactionPress,
  onReplyPress,
  loading = false,
  hasMore = true,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);

  // Sort messages by creation date (newest first, but we'll reverse for display)
  const sortedMessages = useMemo(() => {
    return [...messages].sort(
      (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    );
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [sortedMessages.length]);

  // Handle scroll for loading older messages
  const handleScroll = useCallback(async (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    
    // Check if scrolled to top (for loading older messages)
    if (scrollTop === 0 && hasMore && !loading && !isLoadingMore.current) {
      isLoadingMore.current = true;
      try {
        await onLoadMore();
      } finally {
        isLoadingMore.current = false;
      }
    }
  }, [hasMore, loading, onLoadMore]);

  // Message grouping logic (same as mobile)
  const shouldGroupWithPrevious = useCallback(
    (current: any, previous: any) => {
      if (!previous) return false;

      const timeDiff = Math.abs(
        new Date(current.createdOn).getTime() - 
        new Date(previous.createdOn).getTime()
      );

      return current.fromUID === previous.fromUID && timeDiff < 300000; // 5 minutes
    },
    []
  );

  if (loading && sortedMessages.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading messages...
        </Typography>
      </Box>
    );
  }

  if (sortedMessages.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        flexDirection: 'column',
        gap: 1,
        px: 3
      }}>
        <Typography variant="h6" color="text.secondary">
          No messages yet
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Start a conversation by sending a message below
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={listRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '12px 0',
        backgroundColor: '#ffffff',
        background: 'linear-gradient(to bottom, #f6f7fb 0%, #ffffff 100%)',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      {/* Loading indicator for older messages */}
      {loading && sortedMessages.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: 2 
        }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Messages */}
      {sortedMessages.map((message, index) => {
        const isCurrentUser = message.fromUID === currentUserId;
        const previousMessage = index > 0 ? sortedMessages[index - 1] : undefined;
        const nextMessage = index < sortedMessages.length - 1 ? sortedMessages[index + 1] : undefined;
        
        // Show user info if this is the first message in a group
        const showUserInfo = !shouldGroupWithPrevious(message, previousMessage);

        return (
          <MessageItem
            key={message.id}
            message={message}
            isCurrentUser={isCurrentUser}
            previousMessage={previousMessage}
            nextMessage={nextMessage}
            showUserInfo={showUserInfo}
            onLongPress={onMessageLongPress}
            onImagePress={onImagePress}
            onReactionPress={onReactionPress}
            onReplyPress={onReplyPress}
            currentUserId={currentUserId}
          />
        );
      })}
    </Box>
  );
});

MessageList.displayName = 'MessageList';

export default MessageList;
